import React, { useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { articlesForRole } from '../data/helpGuides';
import {
  Search, HelpCircle, Compass, Users, PenLine, BookOpen, FileText, Wallet, Calculator, UsersRound,
} from 'lucide-react';

const ICONS = { Compass, Users, PenLine, BookOpen, FileText, Wallet, Calculator, UsersRound };

const renderIntro = (intro) => {
  if (!intro) return null;
  const paras = Array.isArray(intro) ? intro : [intro];
  return paras.map((p, i) => <p key={i} className="text-sm text-slate-600 leading-relaxed mb-2">{p}</p>);
};

export default function HelpCenterPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');

  const categories = useMemo(() => {
    const base = articlesForRole(user?.role);
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base
      .map((cat) => ({
        ...cat,
        articles: cat.articles.filter((a) => {
          const hay = (a.q + ' ' + (Array.isArray(a.intro) ? a.intro.join(' ') : a.intro || '') + ' ' + (a.steps || []).join(' ')).toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((cat) => cat.articles.length > 0);
  }, [user?.role, query]);

  const totalArticles = categories.reduce((n, c) => n + c.articles.length, 0);

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="help-center-page">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2D3E6F] to-[#0d9488] rounded-2xl px-6 py-7 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-white/15 rounded-xl p-2.5"><HelpCircle className="w-6 h-6" /></div>
            <h1 className="text-2xl font-bold">Centro de Ajuda</h1>
          </div>
          <p className="text-white/80 text-sm">Guias passo-a-passo para tirares o máximo do CRM. Mostramos só o que é relevante para o teu perfil.</p>
          <div className="relative mt-4 max-w-lg">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pesquisar (ex: criar lead, assinatura, tarifários)..."
              className="pl-9 bg-white text-slate-800"
              data-testid="help-search"
            />
          </div>
        </div>

        {totalArticles === 0 ? (
          <Card><CardContent className="py-12 text-center text-slate-400">Nenhum guia encontrado para "{query}".</CardContent></Card>
        ) : (
          categories.map((cat) => {
            const Icon = ICONS[cat.icon] || HelpCircle;
            return (
              <div key={cat.id} data-testid={`help-cat-${cat.id}`}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Icon className="w-5 h-5 text-[#2D3E6F]" />
                  <h2 className="text-lg font-semibold text-slate-800">{cat.title}</h2>
                  <span className="text-xs text-slate-400">({cat.articles.length})</span>
                </div>
                <Card>
                  <CardContent className="p-2">
                    <Accordion type="single" collapsible className="w-full">
                      {cat.articles.map((a, idx) => (
                        <AccordionItem key={idx} value={`${cat.id}-${idx}`} className="border-slate-100">
                          <AccordionTrigger className="text-sm font-medium text-slate-700 hover:no-underline px-2 text-left" data-testid={`help-q-${cat.id}-${idx}`}>
                            {a.q}
                          </AccordionTrigger>
                          <AccordionContent className="px-2 pb-3">
                            {renderIntro(a.intro)}
                            {a.steps && (
                              <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-600 mt-1">
                                {a.steps.map((s, si) => <li key={si} className="leading-relaxed">{s}</li>)}
                              </ol>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
}
